import * as React from "react";
import * as ReactQuery from "react-query";

import { LoginForm } from "../login-form-provider/login-form";

import { DataMutationLoginAsyncProvider_QueryKey } from "./data-mutation-login-async-query-key-factory";

export interface DataMutationLoginAsyncConsumerInterface {
  mutateAsync: ThisMutationResult["mutateAsync"];
  status: ThisMutationResult["status"];
}
const defaultValue = {} as unknown as DataMutationLoginAsyncConsumerInterface;
const DataMutationLoginAsyncContext = React.createContext(defaultValue);

type ThisMutationRequest = {
  loginForm: LoginForm;
  loginRecaptcha?: any;
  oneTimePassword?: string;
};

type ThisMutationResult = ReactQuery.UseMutationResult<
  OldMagentoStyleLoginAsyncResponse,
  unknown,
  ThisMutationRequest,
  unknown
>;

interface OldMagentoStyleLoginAsyncRequest_LoginData {
  captcha_form_id: "user_login" | string;
  context: "checkout" | string;
  password: string;
  signed_in: "on" | "off";
  username: string;
  one_time_password: string;
  loginRecaptcha?: any;
}

interface OldMagentoStyleLoginAsyncResponse {
  errors?: any;
  message?: string;
}

interface OldMagentoStyleLoginAsyncResponse_Error {
  message?: string;
}

export const DataMutationLoginAsyncProvider: React.FunctionComponent = (
  props
) => {
  const mutationKey: ReactQuery.MutationKey = React.useMemo(() => {
    return DataMutationLoginAsyncProvider_QueryKey.loginAsync();
  }, []);

  const mutationFn: ReactQuery.MutationFunction<
    OldMagentoStyleLoginAsyncResponse,
    ThisMutationRequest
  > = React.useCallback(async (request) => {
    /**
     * You need to update everything in magento as well when you loginAsync,
     * and so this is doing login not using axios or fetch, but magento's own frontend stack.
     */
    type WrappedResponse =
      | { success?: OldMagentoStyleLoginAsyncResponse }
      | { error?: OldMagentoStyleLoginAsyncResponse_Error };
    const loginResponse = await new Promise<WrappedResponse>((resolve) => {
      (window.require as any)(
        ["Magento_Catalog/js/action/login"],
        function (loginAction: any) {
          const loginData: OldMagentoStyleLoginAsyncRequest_LoginData = {
            captcha_form_id: "user_login",
            context: "checkout",
            password: request.loginForm.password || "",
            signed_in: "on",
            username: request.loginForm.email || "",
            one_time_password: request.oneTimePassword || "",
            loginRecaptcha: request.loginRecaptcha,
          };
          loginAction({
            loginData,
            onSuccess: function (response: OldMagentoStyleLoginAsyncResponse) {
              resolve({ success: response });
            },
            onFailure: function (
              error?: OldMagentoStyleLoginAsyncResponse_Error
            ) {
              resolve({ error });
            },
          });
        }
      );
    });

    const isSuccess = (
      wrappedResponse: WrappedResponse
    ): wrappedResponse is { success: OldMagentoStyleLoginAsyncResponse } => {
      return !!(wrappedResponse as any).success;
    };

    if (isSuccess(loginResponse)) {
      return loginResponse.success;
    }

    throw new Error((loginResponse as any)?.error?.message);
  }, []);

  const mutation = ReactQuery.useMutation(mutationKey, mutationFn, {});

  const value: DataMutationLoginAsyncConsumerInterface = React.useMemo(
    () => ({
      mutateAsync: mutation.mutateAsync,
      status: mutation.status,
    }),
    [mutation, mutation.mutateAsync]
  );
  return (
    <DataMutationLoginAsyncContext.Provider value={value}>
      {props.children || []}
    </DataMutationLoginAsyncContext.Provider>
  );
};

export const useDataMutationLoginAsync = () => {
  const context = React.useContext(DataMutationLoginAsyncContext);
  if (context === defaultValue) {
    throw new Error(
      "useDataMutationLoginAsync must be used within a DataMutationLoginAsyncProvider"
    );
  }
  return context;
};
